import React, { useEffect, useMemo, useState } from 'react';
import NextEventInfo from '@components/NextEventInfo';
import PickupTimeOptions from '@components/PickupTimeOptions';
import ShareButton from '@components/ShareButton';
import config from '@config/index';
import strings from '@config/strings';
import {
  useGoodfyndSite,
  useGoodfyndSiteDispatch,
} from '@features/goodfynd-sites/context/goodfynd-site-context';
import { GoodfyndSiteSection } from '@features/goodfynd-sites/context/goodfynd-site-context/types';
import { scrollToSection } from '@features/goodfynd-sites/utils/goodfynd-sites-util';
import { Button, FlexView, Icon, Image, Loading, Tag, Text } from '@goodfynd/ui';
import { dateUtil } from '@utils/index';
import classnames from 'clsx';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useStores } from 'stores';
import { addClassNames } from 'theme';

import type { GoodfyndSitesBusinessInfoProps } from './types';

import classes from './GoodfyndSitesBusinessInfo.module.css';

const getCurrentUrl = () =>
  typeof window !== 'undefined' ? window.location.href : undefined;

export const GoodfyndSitesBusinessInfo = observer(
  ({ className, ...props }: GoodfyndSitesBusinessInfoProps) => {
    const router = useRouter();
    const {
      orderStore: { event, resetPickupOptions },
    } = useStores();
    const [viewPickupTimeOptions, togglePickupTimeOptions] = useState(false);

    const {
      currentLocation,
      currentPickupLocation,
      isFetchingAppointments,
      isPreview,
      orderingLocations,
      organization: data,
      publicLocations,
    } = useGoodfyndSite();
    const { setCurrentPickupLocation } = useGoodfyndSiteDispatch();

    const location = currentPickupLocation || currentLocation;
    useEffect(() => {
      if (location && location.pickupCapacity > 0) {
        setCurrentPickupLocation(location);
      }
    }, [location, setCurrentPickupLocation]);

    useEffect(() => {
      if (location?.vendorId) {
        resetPickupOptions?.(location.vendorId);
      }
    }, [location?.vendorId, resetPickupOptions]);

    // Convert base64 data URL to object URL for opening in a new tab
    const qrCodeObjectUrl = useMemo(() => {
      if (!data.qrCode?.startsWith('data:')) return undefined;

      try {
        const [header, base64] = data.qrCode.split(',');
        const mime = header.match(/:(.*?);/)?.[1] || 'image/png';
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        return URL.createObjectURL(new Blob([bytes], { type: mime }));
      } catch {
        return undefined;
      }
    }, [data.qrCode]);

    const hasEvents = !!publicLocations[0];

    return (
      <FlexView
        className={classnames(className)}
        direction="column"
        gap={0}
        width="100%"
      >
        <section className={classes.container} {...props}>
          <FlexView direction="column" size={1}>
            <Text className={classes.name} tag="h1">
              {data.businessName}
            </Text>

            <FlexView columnGap={2}>
              {data.cuisines.map((cuisine) => (
                <Tag key={cuisine.id} size="small">
                  {cuisine.name}
                </Tag>
              ))}
            </FlexView>
          </FlexView>

          <aside className={classes.block}>
            <ShareButton
              text={`Check out ${data.businessName} on Goodfynd!`}
              title={data.businessName}
              url={getCurrentUrl()}
            />

            {!!qrCodeObjectUrl && (
              <a
                href={qrCodeObjectUrl}
                rel="noreferrer"
                target="_blank"
                title={`View ${data.businessName} QR code`}
              >
                <Image
                  alt={`${data.businessName} QR code`}
                  height={64}
                  src={qrCodeObjectUrl}
                  width={64}
                />
              </a>
            )}
          </aside>
        </section>

        <section className={classes.container}>
          <Text className={classes.description} tag="pre">
            {data.description}
          </Text>
        </section>

        <div className={classes.nextEventInfo}>
          <NextEventInfo
            location={location?.location}
            subTitle={strings.labels.showingMenu}
            title={strings.labels.pickupLocation}
            directions={
              location?.location &&
              location.lotId &&
              `/events/${location.lotId}`
            }
          >
            {isFetchingAppointments && <Loading />}

            {location && (
              <Icon
                name="clock"
                label={
                  <Text>
                    {dateUtil.format(
                      new Date(location?.start as Date),
                      config.dateFormats.weekDayMonthDay
                    )}
                    &nbsp;
                    {event?.pickupTime ? (
                      `@ ${dateUtil.getTimeDisplay(
                        new Date(event.pickupTime as Date)
                      )}`
                    ) : (
                      <span>
                        from&nbsp;
                        {dateUtil.getTimeDisplay(
                          new Date(location?.start as Date)
                        )}
                        &nbsp;-&nbsp;
                        {dateUtil.getTimeDisplay(
                          new Date(location?.end as Date)
                        )}
                        &nbsp;
                      </span>
                    )}
                    {location?.isPrivate && <Tag label="PRIVATE" />}
                  </Text>
                }
              />
            )}

            {hasEvents ? (
              <aside
                className={addClassNames(classes.ctaContainer, {
                  [classes.shift]: !event?.scheduleId && !!publicLocations?.[0],
                })}
              >
                {event?.pickupTime && (
                  <Button
                    className={classes.cta}
                    onClick={() => togglePickupTimeOptions(true)}
                    size="small"
                  >
                    {strings.labels.changePickupTime}
                  </Button>
                )}

                {publicLocations?.[0] && (
                  <Button
                    className={classes.cta}
                    iconLeft="calendar"
                    rank="tertiary"
                    size="small"
                    onClick={() =>
                      scrollToSection(GoodfyndSiteSection.Schedule)
                    }
                  >
                    {strings.labels.viewFullSchedule}
                  </Button>
                )}
              </aside>
            ) : (
              isPreview && (
                <aside className={classes.ctaContainer}>
                  <Button
                    className={classes.cta}
                    color="neutral"
                    href={`${config.env.VENDOR_APP_URL}/dashboard/~/schedule/list`}
                    size="small"
                  >
                    Configure Schedule
                  </Button>
                </aside>
              )
            )}
          </NextEventInfo>
        </div>

        {!!orderingLocations[0] && viewPickupTimeOptions && (
          <PickupTimeOptions
            close={() => togglePickupTimeOptions(false)}
            open={viewPickupTimeOptions}
            schedule={orderingLocations}
            vendorId={location?.vendorNameId ?? ''}
            onContinue={(_, event) => {
              togglePickupTimeOptions(false);
              router.replace(
                {
                  pathname: `/schedules/${event.id}`,
                },
                undefined,
                { shallow: true }
              );
            }}
          />
        )}
      </FlexView>
    );
  }
);
